import React, { useState, useEffect } from 'react';
import {
  Collapse, Fade,
  Card, CardBody, CardHeader, Button, Row, Col, Progress, Nav, NavItem, NavLink, Modal, ModalHeader, ModalBody, ModalFooter, TabContent, TabPane
} from 'reactstrap';
import classnames from 'classnames';
import { Phone, MessageSquare, Clock, Briefcase, Package, Airplay, PhoneCall, Mic } from 'react-feather';
import { useSelector } from 'react-redux';
import './TicketDetail.css';

// Conversation Sample
const conversation = [
  { role: 'Customer', text: 'Hi, I have a question about my card. Can you help me with the card number and name?' },
  { role: 'Representative', text: 'Of course, could you please provide your card number and name?' },
  { role: 'Customer', text: "It's Patrin. I have an older self-card. I was told I would get 12 airport transfers, but when I try to do it, it's limited to a maximum number." },
  { role: 'Representative', text: 'Alright, let me clarify. You have a reserve card, correct?' },
  { role: 'Customer', text: "Yes, that's right." },
  { role: 'Representative', text: 'Okay, on the reserve card, you get four complimentary airport transfers and eight complimentary meet and assist services.' },
  { role: 'Customer', text: 'Ah, I see. So, it\'s not 12 transfers, it\'s a maximum of 12 services, including meet and assist.' },
  { role: 'Representative', text: 'Exactly. Now, let\'s check your usage. Could you please provide the first eight digits of your card number?' },
  { role: 'Customer', text: 'Oh, I don\'t have the card details with me right now.' },
  { role: 'Representative', text: 'No worries, we can check it another way. So, on the reserve card, you\'ve utilized all four airport transfer services.' },
  { role: 'Customer', text: 'I see. Can I cancel one booking and make a new one instead?' },
  { role: 'Representative', text: 'Unfortunately, since you\'ve already utilized all four services, any new bookings would be on a chargeable basis.' },
  { role: 'Customer', text: 'Oh, I understand. In that case, I\'d like to cancel a booking for Polka and make a new one for Bangles.' },
  { role: 'Representative', text: 'Sure, let me check. It seems you\'ve already utilized all four services, so any new bookings would be charged.' },
  { role: 'Customer', text: 'Okay, I understand. So, can I cancel and rebook the upcoming service on 29th November?' },
  { role: 'Representative', text: 'Let me check. Yes, you can cancel and rebook, but please note that the services for your reserve card have been consumed for this calendar year.' },
  { role: 'Customer', text: 'Got it. Thank you for your assistance.' },
  { role: 'Representative', text: 'You\'re welcome. If you have any further questions, feel free to ask. Have a great day!' },
];

const TicketDetailPage = () => {
  const [ticketStatus] = useState('in-progress');
  const [progressValue] = useState(50);
  const [activeTab, setActiveTab] = useState('1');
  const [isOverlayOpen, setIsOverlayOpen] = useState(false); // Control overlay visibility
  const [isRecording, setIsRecording] = useState(false); // To track recording state
  const [transcriptIndex, setTranscriptIndex] = useState(0); // To control conversation transcript animation
  const [typedText, setTypedText] = useState(''); // Word-by-word typing effect
  const [typingInterval, setTypingInterval] = useState(null); // Store interval for typing
  const [analysisVisible, setAnalysisVisible] = useState(false); // To show analysis insights
  const [recommendationsVisible, setRecommendationsVisible] = useState(false); // To show recommendations
  const [isGlowing, setIsGlowing] = useState(false); // New state for glowing effect
  const [fadeIn, setFadeIn] = useState(false); // New state for fade-in effect

  // Example dynamic data fetched from Redux store
  const ticketDetail = useSelector((state) => state.tickets.ticketDetail);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      if (tab === '2') {
        setFadeIn(true);  // Trigger fade-in when "Recommended Products" tab is clicked
      }
    }
  };

  // Toggle Overlay
  const toggleOverlay = () => {
    setIsOverlayOpen(!isOverlayOpen);
  };

  const renderProductCard = (product, index) => (
    <Card className="mb-4" style={{ width: '100%' }}>
      <img src={product.image} alt={product.title} className="card-img-top" style={{ height: '150px', objectFit: 'cover' }} />
      <CardBody>
        <h5 className="card-title">{product.title}</h5>
        <p className="card-text">{product.description}</p>
      </CardBody>
    </Card>
  );

  // Toggle Recording State
  const toggleRecording = () => {
    setIsRecording(!isRecording);
    setIsGlowing(true); // Start glowing when recording begins

    if (!isRecording) {
      startTranscriptAnimation();
    } else {
      resetTranscriptAnimation(); // If recording is stopped, reset the transcript and stop glowing
      setIsGlowing(false); // Stop glowing when recording is stopped
    }
  };

  const analyzeConversation = () => {
    const insights = [
      "The customer is a frequent traveler who utilizes premium services like airport transfers and meet-and-assist.",
      "They have a good understanding of card benefits but may not be fully aware of detailed usage limits.",
      "The customer is conscious of managing their benefits efficiently."
    ];

    const recommendations = [
      {
        title: "Discounted Airport Transfer Packages",
        description: "We offer flexible options at competitive rates for frequent travelers.",
        image: "https://daimahotels.com/public/upload/paketler/4fab6504276a2547a7a271649eb3df8f.webp"
      },
      {
        title: "Exclusive Travel Perks",
        description: "Complimentary hotel upgrades or dining experiences at top restaurants.",
        image: "https://blog.fusionmedstaff.com/hs-fs/hubfs/FMS%20Blog%20Images/GettyImages-1653119080-min.jpg?width=785&height=523&name=GettyImages-1653119080-min.jpg"
      },
      {
        title: "Platinum Tier Upgrade",
        description: "Unlimited airport transfers and priority assistance.",
        image: "https://blogbox.indianeagle.com/wp-content/uploads/2014/07/services-at-plaza-premium-lounges-indira-gandhi-international-airport.jpg"
      }
    ];

    return { insights, recommendations };
  };

  const startTranscriptAnimation = () => {
    let currentDialogueIndex = 0;
    let currentText = '';

    const animateNextDialogue = () => {
      if (currentDialogueIndex >= conversation.length) {
        clearInterval(typingInterval);
        setAnalysisVisible(true);  // Show insights when the conversation completes
        setRecommendationsVisible(true); // Show recommendations when the conversation completes
        return;
      }

      const currentDialogue = conversation[currentDialogueIndex].text;

      if (currentText.length < currentDialogue.length) {
        currentText += currentDialogue[currentText.length];
        setTypedText(currentText);
      } else {
        currentDialogueIndex++;
        currentText = '';
        setTranscriptIndex(currentDialogueIndex);
        setTypedText('');
      }
    };

    const interval = setInterval(animateNextDialogue, 30);
    setTypingInterval(interval);
  };

  const resetTranscriptAnimation = () => {
    setTypedText('');
    setTranscriptIndex(0);
    clearInterval(typingInterval);

    // Hide insights and recommendations when recording stops
    setAnalysisVisible(false);
    setRecommendationsVisible(false);
  };

  useEffect(() => {
    const conversationDiv = document.querySelector('.conversation-container');
    if (conversationDiv) {
      conversationDiv.scrollTop = conversationDiv.scrollHeight; // Automatically scroll to bottom
    }
  }, [typedText, transcriptIndex]);

  useEffect(() => {
    return () => {
      clearInterval(typingInterval);
    };
  }, [typingInterval]);

  return (
    <div className="container-fluid px-5 py-4">
      <Card className="shadow-sm mb-4">
        <CardHeader className="d-flex justify-content-between align-items-center py-3 px-4">
          <h5 className="mb-0 font-weight-bold">
            {ticketDetail.Customer.first_name} {ticketDetail.Customer.last_name} - #{ticketDetail.ticket_id}
          </h5>
          <div>
            <Button outline color="primary" className="me-2">Assign to Agent</Button>
            <Button outline color="secondary" className="me-2">Reassign Ticket</Button>
            <Button color="danger" className="me-2">Close Ticket</Button>
            <Button color="primary" onClick={toggleOverlay}><PhoneCall /></Button>
          </div>
        </CardHeader>
        <CardBody className="p-4">
          <Row>
            <Col md="6">
              <p className="mb-1">
                <strong>Ticket Type:</strong> <Briefcase className="me-2" />{ticketDetail.serviceDetails.name}
              </p>
              <p className="text-muted mb-1">Created: {new Date(ticketDetail.created_at).toLocaleDateString()}</p>
              <p className="text-muted mb-1">Status: {ticketStatus}</p>
            </Col>
            <Col md="6">
              <p className="mb-1"><strong>Contact Information:</strong></p>
              <p className="text-muted mb-1">Email: {ticketDetail.Customer.email}</p>
              <p className="text-muted mb-1">Phone: {ticketDetail.Customer.phone || 'Not provided'}</p>
            </Col>
          </Row>
          <div className="mt-4">
            <p className="mb-1"><strong>Service Details:</strong></p>
            <p className="text-muted mb-1"><Airplay className="me-2" />Airport: JFK International Airport</p>
            <p className="text-muted mb-1"><Package className="me-2" />Flight: AA1234</p>
            <p className="text-muted"><Briefcase className="me-2" />Baggage Claim: Area B</p>
          </div>
        </CardBody>
      </Card>

      <Card className="shadow-sm mb-4">
        <CardHeader className="py-3 px-4">
          <h5 className="mb-0 font-weight-bold">Ticket Progress</h5>
        </CardHeader>
        <CardBody className="p-4">
          <Progress value={progressValue} className="mb-3" />
          <div className="d-flex justify-content-between">
            <span className="text-muted small">Received</span>
            <span className="text-muted small">Processing</span>
            <span className="text-muted small">In Review</span>
            <span className="text-muted small">Resolved</span>
          </div>
          <div className="mt-4">
            <p className="mb-1"><strong>Latest Update:</strong></p>
            <p className="text-muted">Agent reviewing claim documentation - {new Date(ticketDetail.last_updated).toLocaleDateString()}</p>
          </div>
        </CardBody>
      </Card>

      <Modal isOpen={isOverlayOpen} toggle={toggleOverlay} className="modal-right" size="xl">
        <ModalHeader toggle={toggleOverlay}>Agent Interaction Window</ModalHeader>
        <ModalBody>
          <Card className="shadow-sm mb-4">
            <CardBody className="p-4">
              <Nav tabs className="mb-3">
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '1' })}
                    onClick={() => { toggleTab('1'); }}
                  >
                    Call window
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '2', glow: isGlowing })}
                    onClick={() => { toggleTab('2'); }}
                  >
                    Call Analysis
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '3' })}
                    onClick={() => { toggleTab('3'); }}
                  >
                    Call Logs
                  </NavLink>
                </NavItem>
              </Nav>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                    <Button
                      color={isRecording ? 'danger' : 'secondary'}
                      className={`rounded-circle ${isRecording ? 'pulse' : ''}`}
                      onClick={toggleRecording}
                      style={{ width: '100px', height: '100px', fontSize: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                      <Mic />
                    </Button>
                  </div>

                  <div className="mt-4 conversation-container">
                    {conversation.slice(0, transcriptIndex + 1).map((dialogue, idx) => (
                      <div key={idx}>
                        <strong>{dialogue.role}:</strong> {idx === transcriptIndex ? typedText : dialogue.text}
                      </div>
                    ))}
                  </div>
                </TabPane>

                <TabPane tabId="2">
                  <div className="mt-3 mb-4">
                    <h5>Customer Analysis Insights:</h5>
                    <ul>
                      {analyzeConversation().insights.map((insight, idx) => (
                        <Fade in={fadeIn} timeout={7000} style={{ transitionDelay: `${idx * 7000}ms` }} key={idx}>
                          <li>{insight}</li>
                        </Fade>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-3">
                    <h5>Personalized Recommendations:</h5>
                    <div className="recommendation-container" style={{ maxHeight: '300px', overflowY: 'auto' }}> {/* Fixed height and scrollable */}
                      <div className="row">
                        {analyzeConversation().recommendations.map((product, index) => (
                          <Fade in={fadeIn} timeout={7000} style={{ transitionDelay: `${index * 7000}ms` }} key={product.title}>
                            <div className="col-md-12"> {/* Max 2 products per row */}
                              {renderProductCard(product, index)}
                            </div>
                          </Fade>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabPane>


                <TabPane tabId="3">
                  <div className="mt-3">
                    <div className="d-flex align-items-center mb-2">
                      <Phone className="me-2" />
                      <span>May 15, 2023 - 10:30 AM: Initial customer contact</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <MessageSquare className="me-2" />
                      <span>May 15, 2023 - 2:15 PM: Follow-up email sent</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <Clock className="me-2" />
                      <span>May 16, 2023 - 9:00 AM: Scheduled callback</span>
                    </div>
                  </div>
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleOverlay}>Close</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default TicketDetailPage;
